<template>
    <div>
        <Toolbar>
            <BackButton :to="offer.name" :href="$route('offers.edit', offer.id)"/>

            <template v-slot:right>
                <button
                    class="btn btn-bg-white btn-text-dark-50 btn-icon-danger btn-hover-danger font-weight-bold px-5"
                    :disabled="paginatedArticles.data.isEmpty() || search && search.length > 0"
                    @click="removeAllArticles"
                >
                    <i class="fas fa-minus-square"/>
                    {{ __('actions.remove_thing', {thing: __('general.articles_all')}) }}
                </button>
                <button
                    class="btn btn-bg-white btn-text-dark-50 btn-icon-primary btn-hover-primary font-weight-bold px-5"
                    @click="addArticleModalVisible = true"
                >
                    <i class="fas fa-plus-square"/>
                    {{ __('actions.add_thing', {thing: __('general.articles')}) }}
                </button>
            </template>
        </Toolbar>

        <AddOfferArticlesModal :offer="offer"
                               :visible="addArticleModalVisible"
                               @update:visible="v => addArticleModalVisible = v"
        />

        <Card>
            <template v-slot:icon>
                <div v-if="loading" class="spinner spinner-primary mr-6"/>
                <i v-else class="fas fa-tshirt"/>
            </template>
            <template v-slot:title>
                {{ __('offers.manage_articles_for', {offer: offer.name}) }}
            </template>
            <template v-slot:description>
                {{ __('offers.add_or_remove_articles') }}
            </template>
            <template v-slot:toolbar>
                <div class="d-flex flex-lg-row gap-2">
                    <button v-if="paginatedArticles.data.isNotEmpty()"
                            class="btn btn-light-danger"
                            :disabled="selectedEans.isEmpty()"
                            @click="removeSelectedArticles"
                    >
                        <i class="fas fa-trash"/>
                        {{ __('actions.remove_thing', {thing: __('general.articles_selected')}) }}
                    </button>
                    <SearchInput class="w-lg-250px"
                                 :input-classes="{'border-danger': search && search.length > 0}"
                                 :value="search"
                                 @input="startSearch"
                    />
                </div>
            </template>

            <div v-if="paginatedArticles.data.isNotEmpty()">
                <div class="table-responsive">
                    <table class="table table-hover table-vertical-center">
                        <thead>
                        <tr>
                            <th class="w-50px"/>
                            <th colspan="2">{{ __('general.article') }}</th>
                            <th>{{ __('general.ean') }}</th>
                            <th>{{ __('general.brand') }}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="article in paginatedArticles.data"
                            :key="article.ean"
                            class="cursor-pointer"
                            :class="{'bg-light-primary': selectedEans.includes(article.ean)}"
                            @click="toggleRow(article.ean)"
                        >
                            <td class="text-center">
                                <div class="checkbox-inline user-select-none pointer-events-none">
                                    <label class="checkbox checkbox-checkbox-primary">
                                        <input type="checkbox"
                                               :ref="`checkbox${article.ean}`"
                                               :checked="selectedEans.includes(article.ean)"
                                               @input="addOrRemoveArticleFromSelection(article.ean, $event.target.checked)"
                                        >
                                        <span/>
                                    </label>
                                </div>
                            </td>
                            <td class="w-50px">
                                <img v-if="article.image_url"
                                     :src="article.image_url"
                                     :alt="article.name"
                                >
                            </td>
                            <td>
                                {{ article.name }}
                            </td>
                            <td>
                                {{ article.ean }}
                            </td>
                            <td>
                                {{ article.brand }}
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>

                <Pagination :pagination="paginatedArticles"
                            :loading="loading"
                />
            </div>
            <div v-else class="text-muted">
                <div v-if="search && search.trim().length > 0">
                    {{ __('general.your_search_no_hits') }}
                </div>
                <div v-else>
                    {{ __('general.things.none_present_yet', {things: __('general.articles')}) }}
                </div>
            </div>
        </Card>
    </div>
</template>

<script>
import Card from "@components/Cards/Card.vue";
import Pagination from "@components/TableComponents/Pagination.vue";
import Checkbox from "@components/Form/Checkbox.vue";
import PasswordInput from "@components/Form/PasswordInput.vue";
import SearchInput from "@components/Form/SearchInput.vue";
import Toolbar from "@components/Toolbar.vue";
import BackButton from "@components/BackButton.vue";
import Modal from "@components/Modal.vue";
import FormGroupNew from "@components/Form/FormGroupNew.vue";
import AddOfferArticlesModal from "@components/SelectedOffers/AddOfferArticlesModal.vue";

export default {
    components: {
        AddOfferArticlesModal,
        FormGroupNew, Modal, BackButton, Toolbar, SearchInput, PasswordInput, Checkbox, Pagination, Card
    },

    props: {
        offer: Object,
        paginatedArticles: Object,
        search: String
    },

    data() {
        return {
            selectedEans: [],
            addArticleModalVisible: false,
            loading: false
        }
    },

    methods: {
        startSearch(query) {
            let trimmerQuery = query.trim();
            let routeParameters = {
                offer: this.offer.id,
            };

            if (trimmerQuery.length > 0) {
                routeParameters.search = trimmerQuery;
            }

            this.loading = true;
            this.$inertia.get(this.$route('offers.articles.edit', routeParameters), {}, {
                preserveState: true,
                onFinish: () => {
                    this.loading = false;
                }
            })
        },

        addOrRemoveArticleFromSelection(ean, shouldBeAdded) {
            if (shouldBeAdded) {
                this.selectedEans.pushUnique(ean)
            } else {
                this.selectedEans.removeElement(ean)
            }
        },

        toggleRow(ean) {
            this.$refs[`checkbox${ean}`][0].click();
        },

        removeSelectedArticles() {
            this.$inertia.post(this.$route('offers.articles.update', this.offer.id), {
                action: 'remove',
                eans: this.selectedEans
            }, {
                preserveState: true,
                onSuccess: () => {
                    this.selectedEans = [];
                }
            })
        },

        removeAllArticles() {
            Swal.fire({
                title: __('actions.remove_thing', {thing: __('general.articles_all')}) + '?',
                text: __('offers.n_articles_are_going_to_be_removed', {n: this.paginatedArticles.total}),
                showCancelButton: true,
                confirmButtonText: __('actions.delete'),
                cancelButtonText: __('actions.abort'),
                customClass: {
                    confirmButton: "btn btn-danger m-0",
                },
            }).then((result) => {
                if (result.value) {
                    this.$inertia.delete(this.$route('offers.articles.remove.all', this.offer.id));
                }
            })

        }
    }
}
</script>
